import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import AdSlot from '../components/AdSlot';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
 BookOpen, ArrowLeft, PlayCircle, CheckCircle, Circle, Lock,
 Clock, Award, BookmarkPlus, BookmarkCheck, AlertCircle,
 Sparkles, ChevronDown, ChevronUp, XCircle
} from 'lucide-react';
import AdBanner from '../components/AdBanner';
import HeadlineMetric from '../components/HeadlineMetric';
import PageHero from '../components/PageHero';
import ToolSelector from '../components/ToolSelector';
import { CompletedPill, ModuleCompletePill, CompletionRibbon } from '../components/lms/CompletionBadges';
import useLessonTOC from '../hooks/useLessonTOC';
import PageSEO, { buildCourseSchema } from '../components/PageSEO';
import RelatedOnTopic from '../components/RelatedOnTopic';

/**
 * Compact inline preview of the `<h2>` sections inside a lesson body.
 * Renders a chip strip so the course workspace shows exactly what's
 * inside each lesson, not just the title. Empty when the lesson has
 * no `<h2>`s yet (draft) or only one section (no navigation value).
 */
const LessonSectionsPreview = ({ description }) => {
  const { toc } = useLessonTOC(description || '');
  if (!toc || toc.length < 2) return null;
  return (
    <div
      className="flex flex-wrap gap-1.5 mt-1.5"
      data-testid="lesson-sections-preview"
    >
      {toc.slice(0, 8).map((h, i) => (
        <span
          key={h.id}
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:text-neutral-300"
          title={h.text}
        >
          <span className="text-primary/70 font-bold">{i + 1}</span>
          <span className="line-clamp-1 max-w-[180px]">{h.text}</span>
        </span>
      ))}
      {toc.length > 8 && (
        <span className="text-[10px] text-slate-500">
          +{toc.length - 8} more
        </span>
      )}
    </div>
  );
};
import ContentAccessGate from '../components/access/ContentAccessGate';

const CourseDetail = () => {
 const { courseId } = useParams();
 const navigate = useNavigate();
 const { API } = useContext(AuthContext);
 // Admin-preview mode: when opened inside the CoursePreviewModal
 // iframe, we intercept lesson-tile clicks and postMessage to the
 // parent so the lesson opens in a nested LessonPreviewModal instead
 // of navigating (and busting) the iframe.
 const adminPreview = React.useMemo(() => {
   if (typeof window === 'undefined') return false;
   try { return new URLSearchParams(window.location.search).get('admin_preview') === '1'; }
   catch { return false; }
 }, []);
 const openLesson = React.useCallback((lesson) => {
   if (adminPreview && typeof window !== 'undefined' && window.parent && window.parent !== window) {
     try {
       window.parent.postMessage({
         type:        'admin_preview_lesson',
         lessonId:    lesson.id,
         lessonTitle: lesson.title,
       }, '*');
       return;
     } catch { /* fall through to nav */ }
   }
   navigate(`/lesson/${lesson.id}`);
 }, [adminPreview, navigate]);
 const [course, setCourse] = useState(null);
 const [loading, setLoading] = useState(true);
 const [expandedModules, setExpandedModules] = useState(new Set());
 const [isBookmarked, setIsBookmarked] = useState(false);
 const [isEnrolled, setIsEnrolled] = useState(false);
 const [enrolling, setEnrolling] = useState(false);
 const [dropping, setDropping] = useState(false);
 const [showDropConfirm, setShowDropConfirm] = useState(false);

 useEffect(() => {
 fetchCourse();
 }, [courseId, API]);

 // Auto-refresh when a lesson gets marked complete inside LessonViewer —
 // keeps the module accordion + completion pills honest without needing
 // a manual reload after finishing a lesson.
 useEffect(() => {
 const handler = (e) => {
   if (!e?.detail) return;
   if (!e.detail.course_id || e.detail.course_id === courseId) fetchCourse();
 };
 window.addEventListener('lms:progress-updated', handler);
 return () => window.removeEventListener('lms:progress-updated', handler);
 }, [courseId]);

 const fetchCourse = async () => {
 try {
 setLoading(true);
 const response = await axios.get(`${API}/lms/courses/${courseId}`);
 setCourse(response.data);
 setIsBookmarked(response.data.is_bookmarked);
 setIsEnrolled(!!response.data.is_enrolled);
 
 // Expand first module by default
 if (response.data.modules && response.data.modules.length > 0) {
 setExpandedModules(new Set([response.data.modules[0].id]));
 }
 } catch (error) {
 console.error('Error fetching course:', error);
 toast.error('Failed to load course');
 } finally {
 setLoading(false);
 }
 };

 // Explicit Enroll action — hits /api/lms/enroll to persist an enrollment
 // row so the course lands under "Your courses" on the Learning Board.
 // Handles upgrade + purchase gates gracefully.
 const enrollAndStart = async () => {
 try {
 setEnrolling(true);
 if (!isEnrolled) {
 const { data } = await axios.post(`${API}/lms/enroll`, { course_id: courseId });
 if (data?.requires_upgrade) {
 toast.info('Upgrade to Pro to enroll in this course');
 navigate('/pricing');
 return;
 }
 if (data?.requires_purchase) {
 // Kick off a Stripe Checkout for this paid course.
 try {
 let inf_code = null;
 try { inf_code = window.localStorage.getItem('fda_influencer_code') || null; } catch { /* ignore */ }
 const headers = {};
 const tk = window.localStorage.getItem('token');
 if (tk) headers.Authorization = `Bearer ${tk}`;
 const res = await axios.post(`${API}/checkout/session`, {
 items: [{ kind: 'lms_course', ref_id: courseId, quantity: 1 }],
 origin_url: window.location.origin,
 influencer_code: inf_code,
 }, { headers });
 if (res.data?.url) { window.location.href = res.data.url; return; }
 } catch (err) {
 toast.error(err.response?.data?.detail || 'Could not start course checkout');
 }
 return;
 }
 setIsEnrolled(true);
 toast.success('You\u2019re enrolled — starting your first lesson');
 }
 const firstLesson = course.modules?.[0]?.lessons?.[0];
 if (firstLesson) {
 navigate(`/lesson/${firstLesson.id}`);
 } else {
 toast.info('No lessons available in this course yet.');
 }
 } catch (err) {
 console.error('Enroll failed', err);
 toast.error('Could not enroll — please try again');
 } finally {
 setEnrolling(false);
 }
 };

 // Drop (unenroll) — deletes the enrollment row. Lesson progress stays so
 // students can re-enroll without losing their work.
 const dropCourse = async () => {
 try {
 setDropping(true);
 await axios.delete(`${API}/lms/enrollments/${courseId}`);
 setIsEnrolled(false);
 setShowDropConfirm(false);
 toast.success('Course dropped from your list');
 } catch (err) {
 console.error('Drop failed', err);
 toast.error('Could not drop the course');
 } finally {
 setDropping(false);
 }
 };

 const toggleBookmark = async () => {
 try {
 if (isBookmarked) {
 await axios.delete(`${API}/lms/bookmarks/${courseId}`);
 setIsBookmarked(false);
 toast.success('Bookmark removed');
 } else {
 await axios.post(`${API}/lms/bookmarks`, { course_id: courseId });
 setIsBookmarked(true);
 toast.success('Course bookmarked');
 }
 } catch (error) {
 console.error('Error toggling bookmark:', error);
 toast.error('Failed to update bookmark');
 }
 };

 const toggleModule = (moduleId) => {
 setExpandedModules(prev => {
 const updated = new Set(prev);
 if (updated.has(moduleId)) {
 updated.delete(moduleId);
 } else {
 updated.add(moduleId);
 }
 return updated;
 });
 };

 const tierColors = {
 free: 'from-green-500 to-emerald-500',
 tier_1: 'from-amber-500 to-yellow-500',
 tier_2: 'bg-primary/10 text-primary',
 tier_3: 'bg-primary/10 text-primary'
 };

 const tierLabels = {
 free: 'Free Access',
 tier_1: 'Tier 1 - Professional',
 tier_2: 'Tier 2 - Advanced',
 tier_3: 'Tier 3 - Enterprise'
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800 flex items-center justify-center">
 <p className="text-slate-600 dark:text-neutral-400">Loading course...</p>
 </div>
 );
 }

 if (!course) {
 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800 flex items-center justify-center">
 <Card className="p-8 text-center">
 <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
 <h3 className="text-xl font-semibold text-slate-700 dark:text-neutral-300 mb-2">Course not found</h3>
 <Button onClick={() => navigate('/learning')}>Back to Catalog</Button>
 </Card>
 </div>
 );
 }

 const totalLessons = course.modules?.reduce((sum, module) => sum + (module.lessons?.length || 0), 0) || 0;
 const completedLessons = course.modules?.reduce((sum, module) => 
 sum + (module.lessons?.filter(l => l.completed).length || 0), 0
 ) || 0;
 const progressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

 const courseWords = (course.title || '').trim().split(' ');
 const courseLast = courseWords.pop();
 const courseRest = courseWords.join(' ');
 const heroTitle = courseWords.length
 ? <>{courseRest} <span className="text-amber-brand">{courseLast}</span></>
 : <span className="text-amber-brand">{courseLast || course.title}</span>;

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <PageSEO
   title={course.title}
   description={course.description || course.tagline || `${totalLessons} lessons · ${course.tier || 'freeze drying'} course from Freeze Dry Academy.`}
   path={`/course/${courseId}`}
   type="course"
   image={course.hero_image_url || course.thumbnail_url}
   schema={buildCourseSchema({ ...course, id: courseId })}
 />
 <ToolSelector />
 <div className="relative overflow-hidden">
 <PageHero
 eyebrow="Course"
 eyebrowIcon={BookOpen}
 title={heroTitle}
 description={`${totalLessons} lessons · ${progressPercentage}% complete`}
 backTo="/learning"
 backLabel="Courses"
 testId="course-detail-hero"
 />
 {progressPercentage === 100 && <CompletionRibbon testid="course-hero-ribbon" />}
 </div>
 <div className="container mx-auto px-4 py-8 max-w-6xl">
 {/* Liability Advisory — amber HomeLyph Pro tone */}
 <div className="mb-6 p-4 bg-primary/5 border border-primary/30 rounded-2xl" data-testid="course-disclaimer">
 <div className="flex items-start gap-3">
 <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <h3 className="text-sm font-heading font-extrabold text-foreground mb-1">Educational content advisory</h3>
 <p className="text-xs text-foreground/75 leading-relaxed">
 Peak and Prairie Industries, Freeze Dry Academy, and any of its affiliates are not held liable for how this information or these tools are used.
 You assume all responsibility for the proper operation of equipment and food safety practices.
 </p>
 </div>
 </div>
 </div>

 {/* Family B — Headline metric: course progress at a glance */}
 <div className="mb-6 flex items-start gap-3 flex-wrap" data-testid="course-family-b-metric">
 <div className="flex-1 min-w-[280px]">
 <HeadlineMetric
 label={course.title}
 value={`${progressPercentage}%`}
 valueSuffix="complete"
 hint={progressPercentage === 100
 ? 'Nice work — you\u2019ve finished this course. Earn your badge or dive back in for a refresher.'
 : `${completedLessons} of ${totalLessons} lessons done · pick up where you left off.`}
 sideMetrics={[
 { label: 'Lessons', value: `${totalLessons}` },
 { label: 'Done', value: `${completedLessons}` },
 { label: 'Duration', value: course.duration_minutes ? `${course.duration_minutes} min` : '—' },
 ]}
 testId="course-headline-metric"
 />
 </div>
 {progressPercentage === 100 && (
 <div className="pt-2">
 <CompletedPill size="lg" testid="course-completed-pill" />
 </div>
 )}
 </div>

 {/* Advertisement Banner - Header */}
 <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-4">
 <div className="flex items-center justify-between gap-4">
 <div className="flex items-center gap-3">
 <div className="w-11 h-11 bg-primary text-primary-foreground rounded-xl flex items-center justify-center flex-shrink-0">
 <Sparkles className="w-5 h-5" />
 </div>
 <div>
 <p className="text-sm font-heading font-extrabold text-foreground">
 Complete this course for a certificate
 </p>
 <p className="text-xs text-muted-foreground">
 Earn professional certification and boost your credentials.
 </p>
 </div>
 </div>
 <Button
 size="sm"
 className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap"
 >
 Learn more
 </Button>
 </div>
 </div>

 {/* Course Header */}
 <Card className="mb-6">
 <CardContent className="pt-6">
 <div className="flex flex-col md:flex-row gap-6">
 <div className={`w-24 h-24 bg-gradient-to-br ${tierColors[course.tier]} rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0`}>
 <BookOpen className="w-12 h-12 text-white" />
 </div>
 <div className="flex-1">
 <div className="flex items-start justify-between mb-2">
 <div>
 <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${tierColors[course.tier]} mb-2`}>
 {tierLabels[course.tier]}
 </span>
 <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">{course.title}</h1>
 <div 
 className="prose dark:prose-invert max-w-none text-slate-600 dark:text-neutral-400"
 dangerouslySetInnerHTML={{ __html: course.description }}
 />
 </div>
 <div className="flex items-start gap-2 flex-shrink-0">
 <Button
 size="sm"
 onClick={enrollAndStart}
 disabled={enrolling}
 className="rounded-full font-bold whitespace-nowrap bg-primary text-primary-foreground hover:bg-primary/90"
 data-testid="course-enroll-btn"
 >
 {enrolling
   ? 'Working\u2026'
   : (isEnrolled || completedLessons > 0)
   ? (completedLessons > 0 ? 'Continue course' : 'Start course')
   : 'Enroll & start'}
 </Button>
 {(isEnrolled || completedLessons > 0) && (
 <Button
 variant="outline"
 size="sm"
 onClick={() => setShowDropConfirm(true)}
 className="rounded-full font-bold whitespace-nowrap text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700"
 data-testid="course-drop-btn"
 title="Drop this course from your list"
 >
 <XCircle className="w-4 h-4 mr-1" /> Drop
 </Button>
 )}
 <Button
 variant="outline"
 size="sm"
 onClick={toggleBookmark}
 data-testid="course-bookmark-btn"
 >
 {isBookmarked ? (
 <BookmarkCheck className="w-5 h-5 text-blue-600" />
 ) : (
 <BookmarkPlus className="w-5 h-5" />
 )}
 </Button>
 </div>
 </div>
 
 <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-600 dark:text-neutral-400">
 {course.instructor && (
 <div className="flex items-center gap-2">
 <Award className="w-4 h-4" />
 <span>Instructor: {course.instructor}</span>
 </div>
 )}
 <div className="flex items-center gap-2">
 <PlayCircle className="w-4 h-4" />
 <span>{totalLessons} lessons</span>
 </div>
 {course.duration_minutes && (
 <div className="flex items-center gap-2">
 <Clock className="w-4 h-4" />
 <span>{course.duration_minutes} minutes</span>
 </div>
 )}
 </div>

 {/* Progress Bar */}
 {progressPercentage > 0 && (
 <div className="mt-4">
 <div className="flex items-center justify-between text-sm mb-2">
 <span className="text-slate-600 dark:text-neutral-400">Course Progress</span>
 <span className="font-semibold text-blue-600">{progressPercentage}%</span>
 </div>
 <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
 <div
 className="bg-primary text-primary-foreground h-3 rounded-full transition-all"
 style={{ width: `${progressPercentage}%` }}
 />
 </div>
 <p className="text-xs text-slate-500 dark:text-neutral-500 mt-1">
 {completedLessons} of {totalLessons} lessons completed
 </p>
 </div>
 )}
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Course-level Advertisement */}
 {course.ad_enabled && (
 <AdBanner ad={course} position="default" />
 )}

 {/* Course Content */}
 <Card>
 <CardHeader>
 <CardTitle>Course Content</CardTitle>
 <CardDescription>Click on a module to expand and view lessons</CardDescription>
 </CardHeader>
 <CardContent>
 {course.content_locked ? (
 <ContentAccessGate
 targetLabel={course.title || 'this course'}
 onUnlocked={fetchCourse}
 />
 ) : (
 <div className="space-y-3">
 {course.modules && course.modules.length > 0 ? (
 course.modules.map((module, moduleIndex) => {
 const isExpanded = expandedModules.has(module.id);
 const moduleLessons = module.lessons || [];
 const completedInModule = moduleLessons.filter(l => l.completed).length;
 const moduleProgress = moduleLessons.length > 0 
 ? Math.round((completedInModule / moduleLessons.length) * 100) 
 : 0;

 return (
 <div key={module.id} className="border-2 border-slate-200 dark:border-slate-700 rounded-lg">
 <button
 onClick={() => toggleModule(module.id)}
 className="w-full p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
 data-testid={`module-toggle-${module.id}`}
 >
 <div className="flex items-center gap-3 flex-1 text-left">
 <div className="w-10 h-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center flex-shrink-0">
 <span className="text-white font-bold">{moduleIndex + 1}</span>
 </div>
 <div className="flex-1">
 <h3 className="font-semibold text-slate-900 dark:text-white">{module.title}</h3>
 <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 dark:text-neutral-500">
 <span>{moduleLessons.length} lessons</span>
 <span>{completedInModule} completed</span>
 <span>{moduleProgress}% progress</span>
 {moduleProgress === 100 && moduleLessons.length > 0 && (
 <ModuleCompletePill testid={`module-complete-${module.id}`} />
 )}
 </div>
 </div>
 </div>
 {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
 </button>

 {/* Module description — kept OUTSIDE the toggle button because a
     <div>-with-block-content isn't valid nested inside a <button>
     and some browsers refuse to render it. */}
 {module.description && (
 <div
 className="px-4 pb-3 -mt-1 prose dark:prose-invert max-w-none text-sm text-slate-600 dark:text-neutral-400"
 dangerouslySetInnerHTML={{ __html: module.description }}
 data-testid={`module-description-${module.id}`}
 />
 )}

 {isExpanded && (
 <div className="border-t border-slate-200 dark:border-slate-700">
 {/* Module-level Advertisement */}
 {module.ad_enabled && (
 <div className="p-4">
 <AdBanner ad={module} position="default" />
 </div>
 )}

 {/* Module Rich Content Sections */}
 {module.content_sections && module.content_sections.length > 0 && (
 <div className="p-4 bg-pro-soft border-b border-slate-200 dark:border-slate-700">
 <h4 className="font-semibold mb-3 flex items-center gap-2 text-slate-900 dark:text-white">
 <BookOpen className="w-4 h-4" />
 Module Overview
 </h4>
 <div className="space-y-4">
 {module.content_sections
 .sort((a, b) => a.order - b.order)
 .map((section, sectionIndex) => (
 <div key={section.id || sectionIndex} className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
 {section.title && (
 <h5 className="font-medium text-slate-900 dark:text-white mb-2 flex items-center gap-2">
 <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
 {sectionIndex + 1}
 </span>
 {section.title}
 </h5>
 )}
 
 {/* Rich Text Content */}
 {section.type === 'text' && section.text_content && (
 <div 
 className="prose dark:prose-invert max-w-none text-sm"
 dangerouslySetInnerHTML={{ __html: section.text_content }}
 />
 )}
 
 {/* Video Content */}
 {section.type === 'video' && section.video_url && (
 <div className="aspect-video w-full">
 <iframe
 src={`https://www.youtube.com/embed/${
 section.video_url.includes('youtube.com')
 ? new URL(section.video_url).searchParams.get('v')
 : section.video_url.split('/').pop()
 }`}
 className="w-full h-full rounded-lg"
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 allowFullScreen
 title={section.title}
 />
 </div>
 )}
 
 {/* PDF Content */}
 {section.type === 'pdf' && section.pdf_url && (
 <div>
 <p className="text-sm text-slate-600 dark:text-neutral-400 mb-2">
 📄 PDF Document Available
 </p>
 <Button
 onClick={() => window.open(section.pdf_url, '_blank')}
 variant="outline"
 size="sm"
 >
 View PDF
 </Button>
 </div>
 )}
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Module Support Materials */}
 {(module.support_photos?.length > 0 || module.support_videos?.length > 0 || 
 module.support_links?.length > 0 || module.support_downloads?.length > 0) && (
 <div className="p-4 bg-pro-soft dark:from-blue-900/20 dark:to-cyan-900/20 border-b border-slate-200 dark:border-slate-700">
 <h4 className="font-semibold mb-3 flex items-center gap-2 text-slate-900 dark:text-white">
 <Sparkles className="w-4 h-4" />
 Module Resources
 </h4>
 
 <div className="space-y-4">
 {/* Photos */}
 {module.support_photos?.length > 0 && (
 <div>
 <p className="text-xs font-semibold text-slate-700 dark:text-neutral-300 mb-2">📸 Reference Images</p>
 <div className="grid grid-cols-3 gap-2">
 {module.support_photos.slice(0, 6).map((photoUrl, idx) => (
 <a
 key={idx}
 href={photoUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="aspect-video rounded overflow-hidden border border-slate-300 dark:border-slate-600 hover:border-blue-500"
 >
 <img
 src={photoUrl}
 alt={`Module resource ${idx + 1}`}
 className="w-full h-full object-cover hover:scale-105 transition-transform"
 onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Image'; }}
 />
 </a>
 ))}
 </div>
 </div>
 )}

 {/* Videos */}
 {module.support_videos?.length > 0 && (
 <div>
 <p className="text-xs font-semibold text-slate-700 dark:text-neutral-300 mb-2">🎥 Videos ({module.support_videos.length})</p>
 <div className="flex gap-2 flex-wrap">
 {module.support_videos.slice(0, 3).map((_, idx) => (
 <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded text-xs">
 Video {idx + 1}
 </span>
 ))}
 </div>
 </div>
 )}

 {/* Links */}
 {module.support_links?.length > 0 && (
 <div>
 <p className="text-xs font-semibold text-slate-700 dark:text-neutral-300 mb-2">🔗 Links ({module.support_links.length})</p>
 <div className="space-y-1">
 {module.support_links.slice(0, 3).map((link, idx) => (
 <a
 key={idx}
 href={link.url}
 target="_blank"
 rel="noopener noreferrer"
 className="text-xs text-blue-600 dark:text-blue-400 hover:underline block"
 >
 → {link.title}
 </a>
 ))}
 </div>
 </div>
 )}

 {/* Downloads */}
 {module.support_downloads?.length > 0 && (
 <div>
 <p className="text-xs font-semibold text-slate-700 dark:text-neutral-300 mb-2">📥 Downloads ({module.support_downloads.length})</p>
 <div className="flex gap-2 flex-wrap">
 {module.support_downloads.slice(0, 3).map((download, idx) => (
 <a
 key={idx}
 href={download.url}
 download
 className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded text-xs hover:bg-green-200 dark:hover:bg-green-900/50"
 >
 📄 {download.title}
 </a>
 ))}
 </div>
 </div>
 )}
 </div>
 </div>
 )}
 
 {moduleLessons.length > 0 ? (
 <div className="p-4 space-y-2">
 {moduleLessons.map((lesson, lessonIndex) => (
 <button
 key={lesson.id}
 onClick={() => openLesson(lesson)}
 className="w-full p-3 flex items-center gap-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-left group"
 data-testid={`course-lesson-tile-${lesson.id}`}
 >
 <div className="flex-shrink-0">
 {lesson.completed ? (
 <CheckCircle className="w-5 h-5 text-green-600" />
 ) : (
 <Circle className="w-5 h-5 text-slate-400" />
 )}
 </div>
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <span className="text-xs text-slate-500 dark:text-neutral-500">
 Lesson {lessonIndex + 1}
 </span>
 </div>
 <h4 className="font-medium text-slate-900 dark:text-white group-hover:text-blue-600">
 {lesson.title}
 </h4>
 <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-neutral-500">
 {lesson.content_type === 'video' && (
 <span className="flex items-center gap-1">
 <PlayCircle className="w-3 h-3" />
 Video
 </span>
 )}
 {lesson.duration_minutes && (
 <span className="flex items-center gap-1">
 <Clock className="w-3 h-3" />
 {lesson.duration_minutes}m
 </span>
 )}
 {lesson.has_quiz && (
 <span className="text-amber-600 font-semibold">Quiz</span>
 )}
 </div>
 {/* Section list preview — extract every `<h2>` heading from the
     lesson body so the course TOC shows exactly what's in each
     lesson, not just the title. Renders inline as a compact chip
     row that's obvious at a glance but doesn't dominate the row. */}
 <LessonSectionsPreview description={lesson.description} />
 </div>
 <PlayCircle className="w-5 h-5 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
 </button>
 ))}
 </div>
 ) : (
 <div className="p-4 text-center text-slate-500 dark:text-neutral-500 text-sm">
 No lessons available yet
 </div>
 )}
 </div>
 )}
 </div>
 );
 })
 ) : (
 <div className="text-center py-8 text-slate-500 dark:text-neutral-500">
 <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p>No modules available yet</p>
 </div>
 )}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Advertisement Banner - Footer */}
 <div className="mt-8 p-6 bg-pro-soft rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
 <div className="text-center mb-4">
 <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">
 🌟 Continue Your Learning Journey
 </h3>
 <p className="text-sm text-blue-700 dark:text-blue-300">
 Access more courses and unlock advanced training with premium membership
 </p>
 </div>
 <div className="text-center">
 <Button
 className="bg-blue-600 hover:bg-blue-700 text-white"
 onClick={() => navigate('/pricing')}
 >
 Upgrade Membership
 </Button>
 </div>
 </div>

{/* Related on this topic — cross-links to sibling courses + articles
    under the same topic hub. Silently hides if no matches. */}
<RelatedOnTopic
  category={course.category}
  themes={[...(course.themes || []), ...(course.skill_tags || [])]}
  title={course.title}
  excludeCourseId={courseId}
/>


 {/* Sponsor slot at the very end of the course page */}
 <div className="mt-4">
 <AdSlot location="course_footer" variant="footer" />
 </div>

 {/* Drop-course confirmation modal */}
 {showDropConfirm && (
 <div
   className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4"
   role="dialog"
   aria-modal="true"
   data-testid="drop-course-modal"
   onClick={() => !dropping && setShowDropConfirm(false)}
 >
   <div
     className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-6"
     onClick={(e) => e.stopPropagation()}
   >
     <div className="flex items-start gap-3">
       <div className="h-10 w-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
         <XCircle className="w-5 h-5" />
       </div>
       <div className="min-w-0">
         <h3 className="font-heading text-lg font-extrabold text-foreground">Drop this course?</h3>
         <p className="text-sm text-muted-foreground mt-1">
           You{'\u2019'}ll be removed from &ldquo;{course.title}&rdquo;. Your lesson progress and
           any earned certificates stay saved, so you can re-enroll anytime.
         </p>
       </div>
     </div>
     <div className="mt-5 flex items-center justify-end gap-2">
       <Button
         variant="outline"
         size="sm"
         onClick={() => setShowDropConfirm(false)}
         disabled={dropping}
         data-testid="drop-course-cancel"
         className="rounded-full font-bold"
       >
         Cancel
       </Button>
       <Button
         size="sm"
         onClick={dropCourse}
         disabled={dropping}
         data-testid="drop-course-confirm"
         className="rounded-full font-bold bg-rose-600 text-white hover:bg-rose-700"
       >
         {dropping ? 'Dropping\u2026' : 'Yes, drop the course'}
       </Button>
     </div>
   </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default CourseDetail;